/**
 * Pure utility functions for the voter import flow.
 * Extracted from VoterStagingImport to enable unit testing.
 */

interface ColumnPatterns {
  /** Whole-header matches, most preferred first. */
  exact: string[];
  /** Substring matches, tried only when no header matches exactly. Each is 4+ characters. */
  contains: string[];
}

/**
 * Voter field → header patterns, written in normalized form (see normalizeHeader).
 * Exact patterns beat substrings so `Residence_Addresses_State` wins over
 * `Voters_StateVoterID`, and the cell phone wins over a landline listed first.
 */
export const COLUMN_PATTERNS: Record<string, ColumnPatterns> = {
  voterIdColumn: { exact: ['lalvoterid', 'voterid', 'voterregistrationid', 'registrationid', 'id'], contains: ['voterid'] },
  firstNameColumn: { exact: ['firstname', 'votersfirstname', 'fname', 'givenname'], contains: ['firstname', 'givenname'] },
  lastNameColumn: { exact: ['lastname', 'voterslastname', 'lname', 'surname'], contains: ['lastname', 'surname'] },
  middleNameColumn: { exact: ['middlename', 'votersmiddlename', 'mname'], contains: ['middlename'] },
  addressColumn: {
    exact: ['residenceaddressesaddressline', 'address', 'streetaddress', 'addressline', 'addressline1', 'street'],
    contains: ['streetaddress', 'addressline', 'address', 'street'],
  },
  cityColumn: { exact: ['residenceaddressescity', 'city', 'municipality', 'town'], contains: ['city', 'municipality'] },
  stateColumn: {
    exact: ['residenceaddressesstate', 'state', 'st', 'statecode', 'province'],
    contains: ['addressesstate', 'residencestate'],
  },
  zipColumn: {
    exact: ['residenceaddresseszip', 'zip', 'zipcode', 'zip5', 'postalcode', 'postal'],
    contains: ['zipcode', 'postalcode'],
  },
  ageColumn: { exact: ['age', 'votersage', 'voterage'], contains: [] },
  genderColumn: { exact: ['gender', 'votersgender', 'sex'], contains: ['gender'] },
  phoneColumn: {
    exact: [
      'votertelephonescellphoneunformatted', 'votertelephonescellphoneformatted',
      'cellphone', 'cell', 'mobile', 'mobilephone', 'phone', 'phonenumber', 'telephone',
    ],
    contains: ['cellphone', 'mobile', 'phone'],
  },
  emailColumn: { exact: ['email', 'emailaddress', 'votersemail'], contains: ['email'] },
  partyColumn: {
    exact: ['partiesdescription', 'party', 'partyaffiliation', 'politicalparty'],
    contains: ['partyaffiliation', 'politicalparty', 'party'],
  },
  voteFrequencyColumn: { exact: ['votefrequency', 'votingfrequency', 'voterfrequency'], contains: ['votefrequency', 'votingfrequency'] },
  ethnicityColumn: {
    exact: ['ethnicgroupsethnicgroup1desc', 'ethnicity', 'race', 'raceethnicity'],
    contains: ['ethnicity', 'ethnic'],
  },
  religionColumn: {
    exact: ['consumerdatareligioncode', 'religion', 'religiousaffiliation', 'faith'],
    contains: ['religion', 'religiousaffiliation'],
  },
  incomeColumn: {
    exact: ['consumerdataestimatedincomerange', 'income', 'incomelevel', 'householdincome', 'incomerange'],
    contains: ['income'],
  },
  latitudeColumn: { exact: ['residenceaddresseslatitude', 'latitude', 'lat', 'geolat', 'ycoord'], contains: ['latitude'] },
  longitudeColumn: {
    exact: ['residenceaddresseslongitude', 'longitude', 'lng', 'lon', 'geolng', 'xcoord'],
    contains: ['longitude'],
  },
  voterSupportColumn: { exact: ['votersupport', 'supportlevel', 'candidatesupport'], contains: ['votersupport', 'supportlevel'] },
  lastContactStatusColumn: { exact: ['lastcontactstatus', 'lastcontact', 'contactstatus'], contains: ['lastcontact', 'contactstatus'] },
  geocodingAccuracyColumn: {
    exact: ['residenceaddresseslatlongaccuracy', 'latlongaccuracy', 'geocodingaccuracy', 'geoaccuracy'],
    contains: ['latlongaccuracy', 'geocodingaccuracy', 'geoaccuracy'],
  },
  smsConsentStatusColumn: { exact: ['smsconsent', 'smsoptin', 'textconsent', 'smsstatus'], contains: ['smsconsent', 'smsoptin', 'textconsent'] },
  educationColumn: { exact: ['consumerdataeducationofperson', 'education'], contains: ['education'] },
  conservativeDonorColumn: {
    exact: ['consumerdatadonorpoliticalconservative', 'donorpoliticalconservative', 'conservativedonor'],
    contains: ['donorpoliticalconservative', 'conservativedonor'],
  },
  liberalDonorColumn: {
    exact: ['consumerdatadonorpoliticalliberal', 'donorpoliticalliberal', 'liberaldonor'],
    contains: ['donorpoliticalliberal', 'liberaldonor'],
  },
};

/**
 * Lowercase and drop every non-alphanumeric character, so the raw upload header
 * ("First Name") and the server's sanitized staging column ("first_name") match alike.
 */
function normalizeHeader(header: string): string {
  return header.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Auto-detect column mappings from CSV header names. Each field takes at most one
 * column and each column feeds at most one field. Exact matches are claimed first;
 * leftover fields then take substring matches, longest pattern first, so
 * "Voter Ethnicity" goes to ethnicity before city can claim it via "city".
 */
export function autoDetectColumns(columns: string[]): Record<string, string> {
  const mapping: Record<string, string> = {};
  const normalized = columns.map((original) => ({ original, key: normalizeHeader(original) }));
  const claimed = new Set<string>();

  for (const [field, { exact }] of Object.entries(COLUMN_PATTERNS)) {
    for (const pattern of exact) {
      const match = normalized.find((c) => !claimed.has(c.original) && c.key === pattern);
      if (match) {
        mapping[field] = match.original;
        claimed.add(match.original);
        break;
      }
    }
  }

  const candidates = Object.entries(COLUMN_PATTERNS)
    .filter(([field]) => !mapping[field])
    .flatMap(([field, { contains }]) => contains.map((pattern) => ({ field, pattern })))
    .sort((a, b) => b.pattern.length - a.pattern.length);

  for (const { field, pattern } of candidates) {
    if (mapping[field]) continue;
    const match = normalized.find((c) => !claimed.has(c.original) && c.key.includes(pattern));
    if (match) {
      mapping[field] = match.original;
      claimed.add(match.original);
    }
  }

  return mapping;
}

/** Required fields that must be mapped before import can proceed. */
export const REQUIRED_FIELDS = ['firstNameColumn', 'lastNameColumn', 'addressColumn'] as const;

/**
 * Check whether the minimum required columns are mapped.
 */
export function isMappingValid(columnMapping: Record<string, string>): boolean {
  return REQUIRED_FIELDS.every((field) => !!columnMapping[field]);
}
